// Firestore 데이터 관리 서비스
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  setDoc,
  updateDoc, 
  deleteDoc, 
  deleteField,
  query, 
  where, 
  orderBy, 
  onSnapshot,
  writeBatch,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';

// ===== 사용자 관리 =====

// 모든 튜터 조회
export const getAllTutors = async () => {
  try {
    const tutorsRef = collection(db, 'users');
    const q = query(tutorsRef, where('role', '==', 'tutor'));
    const querySnapshot = await getDocs(q);
    
    // 클라이언트에서 정렬 (인덱스 불필요)
    const tutors = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return tutors.sort((a, b) => a.name.localeCompare(b.name));
  } catch (error) {
    console.error('튜터 조회 오류:', error);
    throw error;
  }
};

// 튜터 추가
export const addTutor = async (tutorData) => {
  try {
    const tutorsRef = collection(db, 'users');
    // 임시 비밀번호 자동 발급
    const temp = Math.random().toString(36).slice(-8).toUpperCase();
    const docRef = await addDoc(tutorsRef, {
      ...tutorData,
      role: 'tutor',
      password: temp,
      tempPassword: temp,
      requiresPasswordChange: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return docRef.id;
  } catch (error) {
    console.error('튜터 추가 오류:', error);
    throw error;
  }
};

// 튜터 일괄 추가
export const addTutorsBatch = async (tutorsData) => {
  try {
    const batch = writeBatch(db);
    const tutorsRef = collection(db, 'users');
    
    tutorsData.forEach(tutorData => {
      const docRef = doc(tutorsRef);
      const temp = Math.random().toString(36).slice(-8).toUpperCase();
      batch.set(docRef, {
        ...tutorData,
        role: 'tutor',
        password: temp,
        tempPassword: temp,
        requiresPasswordChange: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    });
    
    await batch.commit();
    return true;
  } catch (error) {
    console.error('튜터 일괄 추가 오류:', error);
    throw error;
  }
};

// 튜터 정보 수정
export const updateTutor = async (tutorId, updateData) => {
  try {
    const tutorRef = doc(db, 'users', tutorId);
    await updateDoc(tutorRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('튜터 수정 오류:', error);
    throw error;
  }
};

// 튜터 삭제
export const deleteTutor = async (tutorId) => {
  try {
    const tutorRef = doc(db, 'users', tutorId);
    await deleteDoc(tutorRef);
    return true;
  } catch (error) {
    console.error('튜터 삭제 오류:', error);
    throw error;
  }
};

// 튜터와 연관된 스케줄 및 메모까지 모두 삭제 (관리자 전용)
export const deleteTutorCascade = async (tutorId) => {
  try {
    // 0) 튜터 문서에서 식별자 수집 (과거 데이터 호환)
    const tutorRef = doc(db, 'users', tutorId);
    const tutorSnap = await getDoc(tutorRef);
    const tutorData = tutorSnap.exists() ? tutorSnap.data() : {};
    const emailKey = tutorData?.email ? String(tutorData.email).toLowerCase() : null;
    const legacyUserIdKey = tutorData?.userId ? String(tutorData.userId) : null;
    const identifierCandidates = [tutorId, emailKey, legacyUserIdKey].filter(Boolean);

    // 1) 튜터 기준 메모 전체 삭제 (매칭대기 메모 포함)
    for (const key of identifierCandidates) {
      const memosByTutorRef = collection(db, 'memos');
      const memosByTutorQuery = query(memosByTutorRef, where('tutorId', '==', key));
      const memosByTutorSnapshot = await getDocs(memosByTutorQuery);
      if (!memosByTutorSnapshot.empty) {
        const batch = writeBatch(db);
        memosByTutorSnapshot.forEach((docSnapshot) => batch.delete(docSnapshot.ref));
        await batch.commit();
      }
    }

    // 2) 해당 튜터의 스케줄 조회 후, 각 스케줄 관련 메모 삭제 -> 스케줄 삭제
    for (const key of identifierCandidates) {
      const schedulesRef = collection(db, 'schedules');
      const schedulesQuery = query(schedulesRef, where('tutorId', '==', key));
      const schedulesSnapshot = await getDocs(schedulesQuery);

      if (!schedulesSnapshot.empty) {
        for (const docSnapshot of schedulesSnapshot.docs) {
          try {
            // 스케줄 연결 메모 삭제
            await deleteScheduleMemos(docSnapshot.id);
          } catch (e) {
            console.error('스케줄 메모 삭제 오류:', e);
          }
        }
        // 스케줄 일괄 삭제
        const batch = writeBatch(db);
        schedulesSnapshot.docs.forEach((docSnapshot) => batch.delete(docSnapshot.ref));
        await batch.commit();
      }
    }

    // 3) 최종적으로 튜터 계정 삭제
    await deleteDoc(tutorRef);

    console.log('✅ 튜터 및 연관 데이터(스케줄/메모) 삭제 완료:', tutorId);
    return true;
  } catch (error) {
    console.error('튜터 연쇄 삭제 오류:', error);
    throw error;
  }
};

// 관리자 계정 생성
export const addAdmin = async (adminData) => {
  try {
    // 이메일 중복 확인
    const usersRef = collection(db, 'users');
    const emailQuery = query(usersRef, where('email', '==', adminData.email.toLowerCase()));
    const emailSnapshot = await getDocs(emailQuery);
    
    if (!emailSnapshot.empty) {
      throw new Error('이미 존재하는 이메일입니다.');
    }
    
    // 관리자 계정 생성
    const docRef = await addDoc(usersRef, {
      ...adminData,
      role: 'admin',
      password: '', // 초기 비밀번호는 빈 값
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    console.log('✅ 관리자 계정 생성 완료:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('관리자 계정 생성 오류:', error);
    throw error;
  }
};

// 사용자 비밀번호 업데이트
export const updateUserPassword = async (userId, newPassword, newName = null, newUserId = null) => {
  try {
    const userRef = doc(db, 'users', userId);
    const updateData = {
      password: newPassword,
      requiresPasswordChange: false,
      tempPassword: deleteField(),
      updatedAt: serverTimestamp()
    };
    
    // 이름도 함께 업데이트하는 경우
    if (newName) {
      updateData.name = newName;
    }
    
    // 아이디도 함께 업데이트하는 경우
    if (newUserId) {
      updateData.userId = newUserId;
    }
    
    await updateDoc(userRef, updateData);
    
    console.log('✅ 사용자 정보 업데이트 완료:', userId);
    return true;
  } catch (error) {
    console.error('사용자 정보 업데이트 오류:', error);
    throw error;
  }
};

// 임시 비밀번호 발급 (관리자 전용)
export const issueTemporaryPassword = async (userId) => {
  try {
    const temp = Math.random().toString(36).slice(-8).toUpperCase(); // 8자리 영문/숫자
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      password: temp,
      tempPassword: temp,
      requiresPasswordChange: true,
      updatedAt: serverTimestamp()
    });
    return temp;
  } catch (error) {
    console.error('임시 비밀번호 발급 오류:', error);
    throw error;
  }
};

// 아이디 중복 확인
export const checkUserIdExists = async (userId) => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error('아이디 중복 확인 오류:', error);
    throw error;
  }
};

// 모든 관리자 조회
export const getAllAdmins = async () => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('role', '==', 'admin'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('관리자 목록 조회 오류:', error);
    throw error;
  }
};

// 관리자 상태 업데이트
export const updateAdminStatus = async (adminId, status) => {
  try {
    const adminRef = doc(db, 'users', adminId);
    await updateDoc(adminRef, {
      status: status,
      updatedAt: serverTimestamp()
    });
    
    console.log('✅ 관리자 상태 업데이트 완료:', adminId, status);
    return true;
  } catch (error) {
    console.error('관리자 상태 업데이트 오류:', error);
    throw error;
  }
};

// 관리자 삭제
export const deleteAdmin = async (adminId) => {
  try {
    const adminRef = doc(db, 'users', adminId);
    await deleteDoc(adminRef);
    
    console.log('✅ 관리자 삭제 완료:', adminId);
    return true;
  } catch (error) {
    console.error('관리자 삭제 오류:', error);
    throw error;
  }
};

// ===== 등록된 이메일 관리 =====

// 등록된 이메일 목록 조회
export const getRegisteredEmails = async () => {
  try {
    const emailsRef = collection(db, 'registeredEmails');
    const querySnapshot = await getDocs(emailsRef);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('등록된 이메일 조회 오류:', error);
    throw error;
  }
};

// 특정 이메일이 등록되어 있는지 확인
export const getRegisteredEmail = async (email) => {
  try {
    const emailsRef = collection(db, 'registeredEmails');
    const q = query(emailsRef, where('email', '==', email.toLowerCase()));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    };
  } catch (error) {
    console.error('등록된 이메일 확인 오류:', error);
    throw error;
  }
};

// 이메일 등록
export const addRegisteredEmail = async (emailData) => {
  try {
    const emailsRef = collection(db, 'registeredEmails');
    const docRef = await addDoc(emailsRef, {
      ...emailData,
      email: emailData.email.toLowerCase(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    console.log('✅ 이메일 등록 완료:', emailData.email);
    return docRef.id;
  } catch (error) {
    console.error('이메일 등록 오류:', error);
    throw error;
  }
};

// 이메일 일괄 등록
export const addRegisteredEmailsBatch = async (emailsData) => {
  try {
    const batch = writeBatch(db);
    const emailsRef = collection(db, 'registeredEmails');
    
    emailsData.forEach(emailData => {
      const docRef = doc(emailsRef);
      batch.set(docRef, {
        ...emailData,
        email: emailData.email.toLowerCase(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    });
    
    await batch.commit();
    console.log('✅ 이메일 일괄 등록 완료:', emailsData.length, '개');
    return true;
  } catch (error) {
    console.error('이메일 일괄 등록 오류:', error);
    throw error;
  }
};

// 등록된 이메일 삭제
export const deleteRegisteredEmail = async (emailId) => {
  try {
    const emailRef = doc(db, 'registeredEmails', emailId);
    await deleteDoc(emailRef);
    
    console.log('✅ 등록된 이메일 삭제 완료:', emailId);
    return true;
  } catch (error) {
    console.error('등록된 이메일 삭제 오류:', error);
    throw error;
  }
};

// ===== 스케줄 관리 =====

// 특정 튜터의 스케줄 조회
export const getTutorSchedules = async (tutorId) => {
  try {
    const schedulesRef = collection(db, 'schedules');
    const q = query(schedulesRef, where('tutorId', '==', tutorId), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('튜터 스케줄 조회 오류:', error);
    throw error;
  }
};

// 모든 스케줄 조회 (관리자용)
export const getAllSchedules = async () => {
  try {
    const schedulesRef = collection(db, 'schedules');
    const querySnapshot = await getDocs(schedulesRef);
    
    // 클라이언트에서 정렬
    const schedules = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return schedules.sort((a, b) => {
      // 먼저 요일로 정렬
      const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      const dayA = dayOrder.indexOf(a.dayOfWeek);
      const dayB = dayOrder.indexOf(b.dayOfWeek);
      
      if (dayA !== dayB) {
        return dayA - dayB;
      }
      
      // 요일이 같으면 시간으로 정렬
      return a.timeSlot.localeCompare(b.timeSlot);
    });
  } catch (error) {
    console.error('전체 스케줄 조회 오류:', error);
    throw error;
  }
};

// 스케줄 추가
export const addSchedule = async (scheduleData) => {
  try {
    const schedulesRef = collection(db, 'schedules');
    const docRef = await addDoc(schedulesRef, {
      ...scheduleData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return docRef.id;
  } catch (error) {
    console.error('스케줄 추가 오류:', error);
    throw error;
  }
};

// 스케줄 일괄 추가
export const addSchedulesBatch = async (schedulesData) => {
  try {
    const batch = writeBatch(db);
    const schedulesRef = collection(db, 'schedules');
    
    schedulesData.forEach(scheduleData => {
      const docRef = doc(schedulesRef);
      batch.set(docRef, {
        ...scheduleData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    });
    
    await batch.commit();
    return true;
  } catch (error) {
    console.error('스케줄 일괄 추가 오류:', error);
    throw error;
  }
};

// 스케줄 상태 업데이트
export const updateScheduleStatus = async (scheduleId, status, studentName = null, updatedBy = null, lessonType = null, lessonDate = null) => {
  try {
    const scheduleRef = doc(db, 'schedules', scheduleId);
    const updateData = {
      status,
      updatedAt: serverTimestamp(),
      updatedBy
    };
    
    if (status === 'confirmed' && studentName) {
      updateData.studentName = studentName;
      updateData.lessonType = lessonType || 'regular'; // 수업 종류 추가
      updateData.lessonDate = lessonDate; // 체험수업 날짜 추가
      updateData.confirmedAt = serverTimestamp();
      updateData.confirmedBy = updatedBy; // 관리자 이름이 포함된 문자열 (예: "이서율 관리자")
    } else if (status === 'pending' || status === 'pending_with_memo') {
      // pending 상태로 변경 시 학생 정보 완전 제거
      updateData.studentName = deleteField();
      updateData.lessonType = deleteField();
      updateData.lessonDate = deleteField();
      updateData.confirmedAt = deleteField();
      updateData.confirmedBy = deleteField();
    }
    
    await updateDoc(scheduleRef, updateData);
    return true;
  } catch (error) {
    console.error('스케줄 상태 업데이트 오류:', error);
    throw error;
  }
};

// 스케줄 삭제
export const deleteSchedule = async (scheduleId) => {
  try {
    const scheduleRef = doc(db, 'schedules', scheduleId);
    await deleteDoc(scheduleRef);
    return true;
  } catch (error) {
    console.error('스케줄 삭제 오류:', error);
    throw error;
  }
};

// 중복 스케줄 정리 (관리자용)
export const cleanupDuplicateSchedules = async () => {
  try {
    const schedulesRef = collection(db, 'schedules');
    const querySnapshot = await getDocs(schedulesRef);
    
    const scheduleMap = new Map();
    const duplicates = [];
    
    querySnapshot.docs.forEach(doc => {
      const data = doc.data();
      const key = `${data.tutorId}-${data.dayOfWeek}-${data.timeSlot}-${data.status}`;
      
      if (scheduleMap.has(key)) {
        // 중복 발견 - 나중에 생성된 것을 삭제 대상으로 추가
        const existing = scheduleMap.get(key);
        const existingTime = existing.createdAt?.toDate?.() || new Date(0);
        const currentTime = data.createdAt?.toDate?.() || new Date(0);
        
        if (currentTime > existingTime) {
          duplicates.push(existing.id);
          scheduleMap.set(key, { id: doc.id, ...data });
        } else {
          duplicates.push(doc.id);
        }
      } else {
        scheduleMap.set(key, { id: doc.id, ...data });
      }
    });
    
    // 중복 스케줄 삭제
    const batch = writeBatch(db);
    duplicates.forEach(scheduleId => {
      const scheduleRef = doc(db, 'schedules', scheduleId);
      batch.delete(scheduleRef);
    });
    
    if (duplicates.length > 0) {
      await batch.commit();
    }
    
    return {
      totalSchedules: querySnapshot.docs.length,
      duplicatesRemoved: duplicates.length,
      cleanSchedules: querySnapshot.docs.length - duplicates.length
    };
  } catch (error) {
    console.error('중복 스케줄 정리 오류:', error);
    throw error;
  }
};

// ===== 메모 관리 =====

// 메모 조회
export const getMemos = async () => {
  try {
    const memosRef = collection(db, 'memos');
    const q = query(memosRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('메모 조회 오류:', error);
    throw error;
  }
};

// 특정 스케줄의 메모 조회
export const getScheduleMemos = async (scheduleId) => {
  try {
    const memosRef = collection(db, 'memos');
    const q = query(memosRef, where('scheduleId', '==', scheduleId), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('스케줄 메모 조회 오류:', error);
    throw error;
  }
};

// 메모 추가
export const addMemo = async (memoData) => {
  try {
    const memosRef = collection(db, 'memos');
    const docRef = await addDoc(memosRef, {
      ...memoData,
      status: 'pending',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return docRef.id;
  } catch (error) {
    console.error('메모 추가 오류:', error);
    throw error;
  }
};

// 메모 상태 업데이트
export const updateMemoStatus = async (memoId, status, reviewedBy = null) => {
  try {
    const memoRef = doc(db, 'memos', memoId);
    const updateData = {
      status,
      updatedAt: serverTimestamp()
    };
    
    if (status === 'approved' || status === 'rejected') {
      updateData.reviewedAt = serverTimestamp();
      updateData.reviewedBy = reviewedBy;
    }
    
    await updateDoc(memoRef, updateData);
    return true;
  } catch (error) {
    console.error('메모 상태 업데이트 오류:', error);
    throw error;
  }
};

// 메모 내용 수정
export const updateMemo = async (memoId, updateData) => {
  try {
    const memoRef = doc(db, 'memos', memoId);
    await updateDoc(memoRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('메모 수정 오류:', error);
    throw error;
  }
};

// 메모 삭제
export const deleteMemo = async (memoId) => {
  try {
    const memoRef = doc(db, 'memos', memoId);
    await deleteDoc(memoRef);
    return true;
  } catch (error) {
    console.error('메모 삭제 오류:', error);
    throw error;
  }
};

// 스케줄 관련 메모 삭제
export const deleteScheduleMemos = async (scheduleId) => {
  try {
    const memosRef = collection(db, 'memos');
    const q = query(memosRef, where('scheduleId', '==', scheduleId));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return 0; // 삭제할 메모가 없음
    }
    
    const batch = writeBatch(db);
    let deletedCount = 0;
    
    querySnapshot.forEach((docSnapshot) => {
      batch.delete(docSnapshot.ref);
      deletedCount++;
    });
    
    await batch.commit();
    console.log(`스케줄 ${scheduleId}의 메모 ${deletedCount}개 삭제 완료`);
    return deletedCount;
  } catch (error) {
    console.error('스케줄 메모 삭제 오류:', error);
    throw error;
  }
};

// ===== 실시간 구독 =====

// 스케줄 실시간 구독
export const subscribeToSchedules = (callback) => {
  const schedulesRef = collection(db, 'schedules');
  const q = query(schedulesRef, orderBy('dayOfWeek'), orderBy('timeSlot'));
  
  return onSnapshot(q, (querySnapshot) => {
    const schedules = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(schedules);
  }, (error) => {
    console.error('스케줄 실시간 구독 오류:', error);
  });
};

// 메모 실시간 구독
export const subscribeToMemos = (callback) => {
  const memosRef = collection(db, 'memos');
  const q = query(memosRef, orderBy('createdAt', 'desc'));
  
  return onSnapshot(q, (querySnapshot) => {
    const memos = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(memos);
  }, (error) => {
    console.error('메모 실시간 구독 오류:', error);
  });
};

// ===== 데이터 백업 및 관리 =====

// 전체 데이터 백업
export const backupAllData = async () => {
  try {
    const collections = ['users', 'schedules', 'memos'];
    const backup = {};
    
    for (const collectionName of collections) {
      const collectionRef = collection(db, collectionName);
      const querySnapshot = await getDocs(collectionRef);
      backup[collectionName] = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    }
    
    return {
      ...backup,
      backupDate: new Date().toISOString(),
      version: '1.0'
    };
  } catch (error) {
    console.error('데이터 백업 오류:', error);
    throw error;
  }
};

// 데이터 복원
export const restoreData = async (backupData) => {
  try {
    const batch = writeBatch(db);
    
    // 기존 데이터 삭제 및 새 데이터 추가는 신중하게 처리
    // 여기서는 기본적인 구조만 제공
    
    Object.keys(backupData).forEach(collectionName => {
      if (['users', 'schedules', 'memos'].includes(collectionName)) {
        const collectionRef = collection(db, collectionName);
        backupData[collectionName].forEach(item => {
          const docRef = doc(collectionRef, item.id);
          batch.set(docRef, item);
        });
      }
    });
    
    await batch.commit();
    return true;
  } catch (error) {
    console.error('데이터 복원 오류:', error);
    throw error;
  }
};

// 체험수업 자동 만료 처리
export const expireTrialLessons = async () => {
  try {
    const now = new Date();
    console.log('현재 시간:', now.toLocaleString());
    
    const schedulesRef = collection(db, 'schedules');
    const q = query(
      schedulesRef,
      where('status', '==', 'confirmed'),
      where('lessonType', '==', 'trial')
    );
    
    const querySnapshot = await getDocs(q);
    const batch = writeBatch(db);
    let expiredCount = 0;
    
    for (const docSnapshot of querySnapshot.docs) {
      const scheduleData = docSnapshot.data();
      
      // lessonDate가 Firestore Timestamp인지 일반 Date인지 확인
      let lessonDate;
      if (scheduleData.lessonDate && typeof scheduleData.lessonDate.toDate === 'function') {
        lessonDate = scheduleData.lessonDate.toDate();
      } else if (scheduleData.lessonDate) {
        lessonDate = new Date(scheduleData.lessonDate);
      } else {
        console.log('lessonDate가 없는 스케줄:', scheduleData);
        continue; // lessonDate가 없으면 건너뛰기
      }
      
      // 체험수업 날짜가 오늘 이전이거나, 오늘인데 수업 시간이 지난 경우 만료 처리
      const lessonDateOnly = new Date(lessonDate.getFullYear(), lessonDate.getMonth(), lessonDate.getDate());
      const todayOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      if (lessonDateOnly < todayOnly) {
        // 어제 이전 날짜의 체험수업은 무조건 만료
        const scheduleRef = doc(db, 'schedules', docSnapshot.id);
        batch.update(scheduleRef, {
          status: 'pending',
          studentName: null,
          lessonType: null,
          lessonDate: null,
          updatedAt: serverTimestamp()
        });
        
        // 관련 메모도 함께 삭제
        try {
          await deleteScheduleMemos(docSnapshot.id);
        } catch (memoError) {
          console.error(`메모 삭제 오류 (스케줄 ${docSnapshot.id}):`, memoError);
        }
        
        expiredCount++;
        console.log(`만료 처리 (과거): ${scheduleData.tutorName} - ${lessonDate.toLocaleDateString()}`);
      } else if (lessonDateOnly.getTime() === todayOnly.getTime()) {
        // 오늘 날짜의 체험수업은 수업 시간이 지났는지 확인
        const timeSlot = scheduleData.timeSlot;
        const endTime = timeSlot.split('-')[1]; // "09:00-10:00" -> "10:00"
        const [endHour, endMinute] = endTime.split(':').map(Number);
        
        const lessonEndTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), endHour, endMinute);
        
        if (now > lessonEndTime) {
          // 수업이 끝난 후 만료 처리
          const scheduleRef = doc(db, 'schedules', docSnapshot.id);
          batch.update(scheduleRef, {
            status: 'pending',
            studentName: null,
            lessonType: null,
            lessonDate: null,
            updatedAt: serverTimestamp()
          });
          
          // 관련 메모도 함께 삭제
          try {
            await deleteScheduleMemos(docSnapshot.id);
          } catch (memoError) {
            console.error(`메모 삭제 오류 (스케줄 ${docSnapshot.id}):`, memoError);
          }
          
          expiredCount++;
          console.log(`만료 처리 (오늘): ${scheduleData.tutorName} - ${lessonDate.toLocaleDateString()} ${timeSlot}`);
        } else {
          console.log(`아직 수업 중: ${scheduleData.tutorName} - ${lessonDate.toLocaleDateString()} ${timeSlot} (종료: ${endTime})`);
        }
      }
    }
    
    if (expiredCount > 0) {
      await batch.commit();
      console.log(`${expiredCount}개의 체험수업이 만료되었습니다.`);
    } else {
      console.log('만료할 체험수업이 없습니다.');
    }
    
    return expiredCount;
  } catch (error) {
    console.error('체험수업 만료 처리 오류:', error);
    throw error;
  }
};

// ===== 일일 초대코드 관리 =====

// 일일 초대코드 생성 및 관리
export const generateDailyInviteCode = async () => {
  try {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD 형식
    const inviteCodeRef = doc(db, 'dailyInviteCodes', today);
    
    // 오늘 날짜의 초대코드가 이미 있는지 확인
    const existingCode = await getDoc(inviteCodeRef);
    
    if (!existingCode.exists()) {
      // 새로운 초대코드 생성 (6자리 랜덤 문자열)
      const code = Math.random().toString(36).substring(2, 8).toUpperCase();
      
      await setDoc(inviteCodeRef, {
        code: code,
        date: today,
        createdAt: serverTimestamp(),
        isActive: true
      });
      
      console.log(`📅 오늘의 초대코드 생성: ${code}`);
      return code;
    } else {
      // 기존 코드 반환
      const codeData = existingCode.data();
      return codeData.code;
    }
  } catch (error) {
    console.error('일일 초대코드 생성 오류:', error);
    throw error;
  }
};

// 초대코드 검증
export const verifyInviteCode = async (code) => {
  try {
    const today = new Date().toISOString().split('T')[0];
    const inviteCodesRef = collection(db, 'dailyInviteCodes');
    const q = query(
      inviteCodesRef, 
      where('code', '==', code.toUpperCase()),
      where('date', '==', today),
      where('isActive', '==', true)
    );
    
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error('초대코드 검증 오류:', error);
    throw error;
  }
};

// 이메일 도메인 검증 (elicer.com만 허용)
export const verifyEmailDomain = (email) => {
  const domain = email.split('@')[1];
  return domain === 'elicer.com';
}; 